import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Product from "../../../types/product";
import Button from "../../tailwind/button";
import Modal from "../../tailwind/modal";
import ModalBody from "../../tailwind/modal-body";
import ModalDialog from "../../tailwind/modal-dialog";
import Row from "../../tailwind/row";
import SuggestItem from "./item";
import SuggestSection from "./section";

interface RequestForm {
  products: number[];
}

export interface IProductSuggestProps {
  show: boolean;
  mainProducts: Product[];
  optionalProducts: Product[];
  relatedProducts: Product[];
  shippingProducts?: Product[];
  selectedShippingId?: number | null;
  onSelectShipping?: (id: number) => void;
  onCancel: () => void;

  onSubmit: (products: number[], shippingId?: number) => void;
}

export default function ProductSuggest({
  show,
  mainProducts,
  optionalProducts,
  relatedProducts,
  shippingProducts = [],
  selectedShippingId,
  onSelectShipping,
  onCancel,
  onSubmit,
}: IProductSuggestProps) {
  const { register, handleSubmit } = useForm<RequestForm>();
  const { t } = useTranslation(undefined, {
    keyPrefix: "ProductSuggest",
  });

  const handleCancel = () => {
    onCancel();
    window.location.reload();
  };

  return (
    <Modal show={show}>
      <ModalDialog>
        <div className="modal-content rounded-lg shadow-lg p-4 bg-white">
          <ModalBody>
            <form
              onSubmit={handleSubmit((f) => {
                const selectedIdentifiers = f.products.map(Number);

                const allProducts = [
                  ...mainProducts,
                  ...optionalProducts,
                  ...relatedProducts,
                  ...shippingProducts,
                ];

                let selectedProducts = allProducts.filter((p) =>
                  selectedIdentifiers.includes(p.identifier)
                );

                if (selectedShippingId) {
                  const shippingProduct = shippingProducts.find(
                    (p) => p.identifier === selectedShippingId
                  );
                  if (shippingProduct) {
                    selectedProducts = selectedProducts.filter(
                      (p) => p.identifier !== shippingProduct.identifier
                    );
                  }
                }

                const productIdentifiers = selectedProducts.map(
                  (p) => p.identifier
                );

                onSubmit(productIdentifiers, selectedShippingId ?? undefined);
              })}
            >
              <table className="w-full mb-6">
                <tbody>
                  {mainProducts.length > 0 && (
                    <>
                      <SuggestSection title={t("YourItems")} />
                      {mainProducts.map((p) => (
                        <SuggestItem
                          key={p.id}
                          register={register("products")}
                          product={p}
                          selected
                          mandatory
                        />
                      ))}
                    </>
                  )}

                  {optionalProducts.length > 0 &&
                    optionalProducts.map((p) => (
                      <SuggestItem
                        key={p.id}
                        register={register("products")}
                        product={p}
                        selected={true}
                      />
                    ))}

                  {relatedProducts.length > 0 && (
                    <>
                      <SuggestSection title={t("RelatedItems")} />
                      {relatedProducts.map((p) => (
                        <SuggestItem
                          key={p.id}
                          register={register("products")}
                          product={p}
                        />
                      ))}
                    </>
                  )}

                  {shippingProducts.length > 0 && (
                    <>
                      <SuggestSection title={t("ShippingMethods")} />
                      {shippingProducts.map((p) => (
                        <tr key={p.id}>
                          <td colSpan={2}>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="shipping"
                                value={p.identifier}
                                checked={selectedShippingId === p.identifier}
                                onChange={() =>
                                  onSelectShipping?.(p.identifier)
                                }
                              />
                              <span>{p.title}</span>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>

              <Row className="row">
                <Button
                  type="submit"
                  className="bg-green-600 col-6 hover:bg-green-700 text-white px-6 py-2 rounded w-1/2"
                  text={t("Submit")}
                />
                <Button
                  className="bg-red-600 col-6 hover:bg-red-700 text-white px-6 py-2 rounded w-1/2"
                  text={t("Cancel")}
                  onClick={handleCancel}
                />
              </Row>
            </form>
          </ModalBody>
        </div>
      </ModalDialog>
    </Modal>
  );
}
